import { TEST_ID_GEMINI_CHAT } from "e2e/testId";
import { MessageCircle, Trash2 } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";

/**
 * Props for ThreadListItemView component
 */
interface ThreadListItemViewProps {
  title: string;
  previewText: string;
  lastAccessed: string;
  messageCount: string;
  isSelected: boolean;
  isLoading: boolean;
  onPress: () => void;
  onDelete: () => void;
}

/**
 * Pure UI component for displaying a single thread item in the thread list
 * Shows thread title, preview text, metadata, and selection state
 * @param root0 - Component props
 * @param root0.title - Thread title or generated title from first question
 * @param root0.previewText - Truncated preview of last answer or first question
 * @param root0.lastAccessed - Formatted last accessed time (e.g., "2h ago", "3d ago")
 * @param root0.messageCount - Formatted message count text (e.g., "5 messages")
 * @param root0.isSelected - Whether this thread is currently selected/active
 * @param root0.isLoading - Whether thread is currently loading (shows spinner)
 * @param root0.onPress - Callback invoked when thread item is pressed
 * @param root0.onDelete - Callback invoked when thread delete action is triggered
 */
const ThreadListItemView = ({
  title,
  previewText,
  lastAccessed,
  messageCount,
  isSelected,
  isLoading,
  onPress,
  onDelete,
}: ThreadListItemViewProps) => (
  <Box
    className="flex flex-row items-center gap-micro px-2 pb-1"
    testID={TEST_ID_GEMINI_CHAT.THREAD_LIST_ITEM}
  >
    {/*
     * The delete Button is a SIBLING of the Pressable, not a child of it.
     * On react-native-web both compile to DOM nodes and a click on a nested
     * pressable bubbles to its ancestor, so nesting the button inside the row
     * would select the thread on the same click that opens the confirm dialog.
     */}
    <Pressable
      onPress={onPress}
      className={`flex-1 rounded-sm p-2 transition-colors md:p-3 ${
        isSelected ? "bg-needs-semantic-primary-50" : "hover:bg-surface-raised"
      }`}
    >
      <Box className="flex-1">
        {/* Title and Actions Row */}
        <Box className="flex flex-row items-start justify-between gap-micro pb-micro">
          <Text
            numberOfLines={1}
            variant="body-strong"
            className={`flex-1 ${
              isSelected
                ? "text-needs-semantic-primary-900"
                : "text-content-primary"
            }`}
          >
            {title}
          </Text>

          {/* Actions */}
          {isLoading && (
            <Box>
              <Spinner
                size="small"
                className="text-needs-semantic-primary-600"
              />
            </Box>
          )}
        </Box>

        {/* Preview Text */}
        <Text
          variant="caption"
          numberOfLines={2}
          className="pb-1 text-content-muted"
        >
          {previewText}
        </Text>

        {/* Metadata Row */}
        <Box className="flex flex-row items-center justify-between">
          <Box className="flex flex-row items-center gap-1">
            <Icon
              as={MessageCircle}
              size="2xs"
              className="text-content-subtle"
            />
            <Text
              variant="caption"
              className={`${
                isSelected
                  ? "text-needs-semantic-primary-600"
                  : "text-content-subtle"
              }`}
            >
              {messageCount}
            </Text>
          </Box>

          <Text variant="micro" className="text-content-subtle">
            {lastAccessed}
          </Text>
        </Box>
      </Box>
    </Pressable>

    <Button
      testID={TEST_ID_GEMINI_CHAT.DELETE_THREAD_BUTTON}
      accessibilityLabel="Delete conversation"
      size="xs"
      variant="link"
      tone="danger"
      icon={Trash2}
      isDisabled={isLoading}
      onPress={onDelete}
    />
  </Box>
);

ThreadListItemView.displayName = "ThreadListItemView";

export default memo(ThreadListItemView);
